import { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "shared-utils";

import type {
  UsePlacesAutocompleteOptions,
  UsePlacesAutocompleteReturn,
  SelectedOption,
  ParsedAddress,
} from "./google-places";
import { PLACES_API_CONFIG } from "./google-places-constants";
import { usePlacesApi } from "./use-places-api";
import { createAddressParser, processAutocompleteOptions } from "./google-places-utils";

export const usePlacesAutocomplete = <T = any>(
  options: UsePlacesAutocompleteOptions<T>
): UsePlacesAutocompleteReturn => {
  const {
    debounceMs = PLACES_API_CONFIG.defaultDebounceMs,
    minQueryLength = PLACES_API_CONFIG.defaultMinQueryLength,
    countries,
    onAddressSelect,
    form,
    fieldName,
  } = options;

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState<SelectedOption | null>(null);
  const [isInitialised, setIsInitialised] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, debounceMs);
  const { fetchPlaceDetails, fetchAutocomplete, cache } = usePlacesApi();
  const parseAddressComponents = useMemo(() => createAddressParser(), []);

  const {
    data: predictions = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["places-autocomplete", debouncedSearchTerm, countries],
    queryFn: () => fetchAutocomplete(debouncedSearchTerm, countries, minQueryLength),
    enabled: !!debouncedSearchTerm && debouncedSearchTerm.length >= minQueryLength,
    staleTime: PLACES_API_CONFIG.staleTime,
  });

  const initialiseFromFormValue = useCallback(async () => {
    if (isInitialised || !form || !fieldName || selectedOption) {
      return;
    }

    const formValue = form.getValues(fieldName);
    if (formValue && typeof formValue === "string") {
      const cachedPlaceDetails = cache.getCachedPlaceDetails(formValue);
      const cachedOriginalText = cache.getCachedOriginalText(formValue);

      if (cachedPlaceDetails && cachedOriginalText) {
        const address = parseAddressComponents(cachedPlaceDetails.addressComponents);
        address.formattedAddress = cachedPlaceDetails.formattedAddress;

        setSelectedOption({
          id: formValue,
          text: cachedOriginalText,
          address,
        });
        setSearchTerm(cachedOriginalText);
        setIsInitialised(true);
        return;
      }

      const placeDetails = await fetchPlaceDetails(formValue);
      if (placeDetails) {
        const address = parseAddressComponents(placeDetails.addressComponents);
        address.formattedAddress = placeDetails.formattedAddress;

        setSelectedOption({
          id: formValue,
          text: placeDetails.formattedAddress,
          address,
        });
        setSearchTerm(placeDetails.formattedAddress);
      }
    }
    setIsInitialised(true);
  }, [isInitialised, form, fieldName, fetchPlaceDetails, parseAddressComponents, cache, selectedOption]);

  useEffect(() => {
    initialiseFromFormValue();
  }, [initialiseFromFormValue]);

  const onSearchTermChange = useCallback(
    (newSearchTerm: string) => {
      if (newSearchTerm === searchTerm) {
        return;
      }

      if (selectedOption && !newSearchTerm) {
        return;
      }

      // If user is typing but no option selected, it's likely manual
      if (newSearchTerm && !selectedOption) {
        setIsManualEntry(true);
      }

      setSearchTerm(newSearchTerm);
    },
    [searchTerm, selectedOption]
  );

  const onChange = useCallback(
    async (option: any) => {
      if (!option) {
        setSelectedOption(null);
        setIsManualEntry(false);
        if (form && fieldName) {
          form.setValue(fieldName, "");
        }
        return;
      }

      setIsManualEntry(false); // Reset manual entry flag

      const initialAddress: ParsedAddress = { formattedAddress: option.text };
      setSelectedOption({
        id: option.id,
        text: option.text,
        address: initialAddress,
      });

      setSearchTerm(option.text);

      const placeDetails = await fetchPlaceDetails(option.prediction.place_id);
      if (placeDetails) {
        const detailedAddress = parseAddressComponents(placeDetails.addressComponents);
        detailedAddress.formattedAddress = placeDetails.formattedAddress;

        cache.cacheOriginalText(option.id, option.text);

        setSelectedOption((prev) =>
          prev
            ? {
                ...prev,
                address: detailedAddress,
              }
            : null
        );

        if (form && fieldName) {
          form.setValue(fieldName, option.id);
        }

        onAddressSelect?.(detailedAddress, placeDetails);
      }
    },
    [fetchPlaceDetails, form, fieldName, parseAddressComponents, cache, onAddressSelect]
  );

  const clear = useCallback(() => {
    setSearchTerm("");
    setSelectedOption(null);
  }, []);

  const autocompleteOptions = useMemo(
    () => processAutocompleteOptions(predictions, selectedOption),
    [predictions, selectedOption]
  );

  return {
    options: autocompleteOptions,
    fetching: isLoading,
    error: error?.message || null,
    onSearchTermChange,
    onChange,
    searchTerm,
    selectedAddress: selectedOption?.address || null,
    selectedDisplayText: selectedOption?.text || null,
    clear,
    isManualEntry,
  };
};

export { createAddressFieldSetters } from "./google-places-utils";
